"use client";

import React, { useEffect, useRef, useState } from "react";
import { PhoneMissed, MessageSquareWarning, Clock, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface AnimatedCounterProps {
  endValue: number;
  isVisible: boolean;
  suffix?: string;
}
function AnimatedCounter({
  endValue,
  isVisible,
  suffix = ""
}: AnimatedCounterProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (isVisible) {
      let start = 0;
      const duration = 2000;
      const frameRate = 1000 / 60;
      const totalFrames = Math.round(duration / frameRate);
      const increment = endValue / totalFrames;
      let currentFrame = 0;
      const timer = setInterval(() => {
        currentFrame++;
        start += increment;
        if (currentFrame >= totalFrames) {
          setCount(endValue);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, frameRate);
      return () => clearInterval(timer);
    }
  }, [isVisible, endValue]);
  return <span className="font-mono text-5xl md:text-7xl font-bold tracking-tighter">
      {count}
      <span className="text-[#B5A642]">{suffix}</span>
    </span>;
}
const leakData = [{
  id: 1,
  title: "After-Hours Leaks",
  value: 62,
  suffix: "%",
  description: "Of inbound calls to plumbing and HVAC companies go to voicemail after 5 PM, resulting in catastrophic revenue loss.",
  icon: PhoneMissed
}, {
  id: 2,
  title: "Dispatch Latency",
  value: 45,
  suffix: "%",
  description: "Of emergency inquiries are answered too late to secure the job. Our AI Bridge ensures zero-latency booking into ServiceTitan.",
  icon: MessageSquareWarning
}, {
  id: 3,
  title: "Follow-up Friction",
  value: 80,
  suffix: "%",
  description: "Lead drop-off rate when follow-up takes longer than 5 minutes. In the high-ticket world, speed to lead is the only metric that matters.",
  icon: Clock
}];
export default function TheRevenueLeakageReality() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="88637" id="the-leaky-pipeline" className="dark bg-[#003153] text-[#F5F5DC] relative overflow-hidden py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-20">
        <div className="bg-[#D97706]/20 h-[500px] w-[500px] rounded-full blur-[120px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <h2 className="font-mono text-[#D97706] mb-4 text-sm font-semibold tracking-widest uppercase">
            Recover the Revenue. Protect the Legacy.
          </h2>
          <h3 className="mb-6 text-4xl font-serif font-bold tracking-tight md:text-5xl lg:text-6xl uppercase">
            The Leaky Pipeline
          </h3>
          <p className="text-[#F5F5DC]/70 text-lg md:text-xl font-sans">
            Every missed call after 5 PM is a burst in your revenue. We repair the break with 24/7 AI Voice Intelligence that triages emergencies and books jobs in real-time.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-3">
          {leakData.map((leak, i) => {
          const {
            ref: cardRef,
            isVisible: isCardVisible
          } = useRevealOnScroll({
            threshold: 0.2
          });
          const Icon = leak.icon;
          return <div key={leak.id} data-index={i} ref={cardRef} className={`transition-all duration-1000 delay-${i * 200} ${isCardVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
                <Card className="bg-[#003153]/60 border-[#F5F5DC]/20 relative h-full rounded-none overflow-hidden backdrop-blur-sm transition-colors hover:bg-[#003153]/80">
                  <CardHeader className="pb-4">
                    <div className="mb-4 flex items-center justify-between">
                      <CardTitle className="font-serif text-xl font-bold uppercase tracking-wider text-[#F5F5DC]">
                        {leak.title}
                      </CardTitle>
                      <div className={`flex size-12 items-center justify-center rounded-none border-2 transition-all duration-1000 ${isCardVisible ? "border-[#B5A642]/50 bg-[#B5A642]/10 text-[#B5A642] shadow-[0_0_20px_rgba(181,166,66,0.3)]" : "border-[#D97706]/50 bg-[#D97706]/10 text-[#D97706] shadow-[0_0_20px_rgba(217,119,6,0.3)]"}`}>
                        <Icon className="size-5" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-6 flex items-baseline gap-1">
                      <AnimatedCounter endValue={leak.value} isVisible={isCardVisible} suffix={leak.suffix} />
                    </div>
                    <p className="text-[#F5F5DC]/70 font-sans leading-relaxed">
                      {leak.description}
                    </p>
                  </CardContent>
                  
                  {}
                  <div className="absolute right-0 bottom-0 left-0 h-1 bg-[#003153]">
                    <div className={`h-full bg-gradient-to-r from-[#D97706] to-[#B5A642] transition-all duration-2000 ease-out ${isCardVisible ? "w-full" : "w-0"}`} />
                  </div>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-20 flex justify-center">
          <div className={`transition-all duration-1000 delay-700 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
            <Link to="/OperationalAuditPage">
              <Button size="lg" className="group h-14 px-8 text-base bg-[#B5A642] text-[#003153] hover:bg-[#B5A642]/90 rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] font-serif font-bold uppercase tracking-wide">
                Begin Operational Audit
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}